import { Duration } from 'luxon';
import { z } from 'zod';

import { createPaginatedResultSchema } from '@/lib/api/schemas/shared/paginated';
import { parseISO8601DateTime, parseISO8601Duration } from '@/utils/dates';

// Custom duration parser instead of zodDuration: zodDuration is pricing-flavoured
// (rejects zero values, recognises 'all_day' / 'until_5am_next_day' / 'usage')
// and would discard the whole visits response on a legitimate "PT0S" duration.
// Matches the parser camera.ts uses for the same reason.
const visitDuration = z
  .string()
  .transform((val) => parseISO8601Duration(val))
  .pipe(
    z.custom<Duration<true>>((val) => val instanceof Duration && val.isValid)
  );

/**
 * Visit status — mirrors permit.VisitStatus in the API.
 *
 * - `completed` covers both fully paired visits and partial ones (entry-only
 *   because we observed a later in, or exit-only with no preceding in).
 * - `in_progress` means the entry is the most recent event for that location;
 *   parker is currently parked as far as we can tell.
 */
// Tolerate forward-compatible status values: if the API ever adds a new
// status, fall back to the raw string instead of throwing away the whole
// response and rendering an empty table.
export const PermitVisitStatusSchema = z
  .enum(['completed', 'in_progress'])
  .or(z.string());
export type PermitVisitStatus = z.infer<typeof PermitVisitStatusSchema>;

const PermitVisitPlateSchema = z.object({
  number: z.string(),
  state: z.string(),
});

/**
 * Visit shape returned by GET /location/permits/:id/visits.
 *
 * The server emits `null` for zero-value chrono.Time and chrono.Duration
 * fields (via the custom MarshalJSON on those types), so EntryAt / ExitAt /
 * Duration come through as `string | null`. Image URLs are presigned by the
 * API and empty when no event is on that side.
 */
export const PermitVisitSchema = z.object({
  id: z.number(),
  status: PermitVisitStatusSchema,
  location_id: z.string(),
  block_face_id: z.string().nullish(),
  plate: PermitVisitPlateSchema,
  entry_event_id: z.string().nullish(),
  entry_at: z
    .string()
    .nullish()
    .transform((val) => (val ? parseISO8601DateTime(val) : undefined)),
  entry_image_url: z.preprocess((val) => val ?? '', z.string()),
  exit_event_id: z.string().nullish(),
  exit_at: z
    .string()
    .nullish()
    .transform((val) => (val ? parseISO8601DateTime(val) : undefined)),
  exit_image_url: z.preprocess((val) => val ?? '', z.string()),
  duration: visitDuration.nullish().transform((v) => v ?? undefined),
});
export type PermitVisit = z.infer<typeof PermitVisitSchema>;

/**
 * Pagination cursor for ListVisits. Composite (EntryAtMillis, EventID) so two
 * visits sharing a millisecond don't silently drop. Treat as opaque on the
 * client — round-trip it back as-is.
 */
export const PermitVisitStartKeySchema = z.object({
  entryAtMillis: z.number(),
  eventId: z.string(),
});
export type PermitVisitStartKey = z.infer<typeof PermitVisitStartKeySchema>;

export const PermitVisitsResultSchema = createPaginatedResultSchema(
  PermitVisitSchema,
  PermitVisitStartKeySchema
);
export type PermitVisitsResult = z.infer<typeof PermitVisitsResultSchema>;
